import classNames from 'classnames';
import ErrorMessage from 'Views/Components/New/ErrorMessage';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { RecurrencePattern, BulkBookingWeekOrdinal } from 'Modules/BulkBooking/Types';
import {
	DAY_OPTIONS,
	FREQUENCY_OPTIONS,
	WEEK_ORDINAL_OPTIONS,
	describeRecurrence,
	intervalUnitLabel,
	normaliseInterval,
} from 'Modules/BulkBooking/Recurrence';

interface RecurrencePatternInputsProps {
	value: RecurrencePattern;
	onChange: (updates: Partial<RecurrencePattern>) => void;
	errors?: Record<string, string>;
	/** Prefix for each control's `data-test-id`, so two screens using these inputs stay distinguishable. */
	testIdPrefix?: string;
	/** Narrow containers (the trip sidebar) stack the frequency and interval instead of pairing them. */
	singleColumn?: boolean;
	/**
	 * Opt-in BEM styling for a screen that draws these inputs its own way (the trip sidebar's Occurrences
	 * tab). Given a block name, every wrapper, label and pill carries `<block>__…` classes instead of the
	 * Tailwind ones, so the caller's stylesheet owns the look. Left out, the output is unchanged.
	 */
	classPrefix?: string;
	/** The caller shows the summary sentence itself — the trip sidebar puts the date range beside it. */
	hideSummary?: boolean;
}

const pillClassName = (isSelected: boolean, extra?: string) =>
	classNames(
		'flex items-center justify-center h-10 rounded-full border text-sm font-medium transition-colors',
		isSelected
			? 'bg-[var(--colour-brand-primary)] border-[var(--colour-brand-primary)] text-white'
			: 'bg-white border-gray-300 text-gray-700 hover:border-gray-400',
		extra,
	);

/**
 * The repeat pattern — frequency, interval, week of month and days — shared by bulk bookings and
 * recurring ferry trips. Both send it to the server's `BulkBookingRecurrence`, so the same choices
 * select the same dates in both places.
 */
export default function RecurrencePatternInputs({
	value,
	onChange,
	errors,
	testIdPrefix = 'bulk-booking',
	singleColumn = false,
	classPrefix,
	hideSummary = false,
}: RecurrencePatternInputsProps) {
	const { frequency, interval, daysOfWeek, weekOrdinals } = value;

	const isMonthly = frequency === 'monthly';

	// The caller's BEM classes when it has opted in, otherwise the Tailwind classes bulk booking is drawn with.
	const cls = (elements: string[], fallback: string) =>
		classPrefix ? elements.map(element => `${classPrefix}__${element}`).join(' ') : fallback;
	const pill = (element: string, isSelected: boolean, extra: string) =>
		classPrefix
			? classNames(`${classPrefix}__${element}`, isSelected && `${classPrefix}__${element}--selected`)
			: pillClassName(isSelected, extra);
	const labelClassName = cls(['label'], 'text-sm text-gray-800');
	const requiredMark = <span className={cls(['required'], 'text-[var(--colour-brand-primary)]')}>*</span>;

	const toggleDay = (day: string) => {
		const selected = new Set(daysOfWeek);
		if (selected.has(day)) {
			selected.delete(day);
		} else {
			selected.add(day);
		}
		onChange({ daysOfWeek: Array.from(selected) });
	};

	const toggleWeekOrdinal = (ordinal: BulkBookingWeekOrdinal) => {
		const selected = new Set(weekOrdinals);
		if (selected.has(ordinal)) {
			selected.delete(ordinal);
		} else {
			selected.add(ordinal);
		}
		onChange({ weekOrdinals: Array.from(selected) });
	};

	const summary = describeRecurrence(value);

	const intervalInput = (
		<NumberInput
			label="Repeat every"
			required
			allowDecimals={false}
			allowNegative={false}
			value={interval}
			onChange={next => onChange({ interval: next ?? 1 })}
			endAdornment={
				<span className={cls(['unit'], 'text-sm text-gray-600')}>
					{intervalUnitLabel(frequency, normaliseInterval(interval))}
				</span>
			}
			error={errors?.interval}
			data-test-id={`${testIdPrefix}-interval`}
		/>
	);

	return (
		<>
			{/* Repeat pattern */}
			<div
				className={cls(
					['frequency-interval'],
					classNames('grid grid-cols-1 gap-4', !singleColumn && 'lg:grid-cols-2'),
				)}
			>
				<div className={cls(['field', 'field--frequency'], 'flex flex-col gap-1 w-full')}>
					<span className={labelClassName}>Repeats {requiredMark}</span>
					<div className={cls(['segmented'], 'flex flex-wrap gap-2')}>
						{FREQUENCY_OPTIONS.map(option => (
							<button
								key={option.value}
								type="button"
								aria-pressed={frequency === option.value}
								onClick={() => onChange({ frequency: option.value })}
								className={pill('segment', frequency === option.value, 'px-5')}
								data-test-id={`${testIdPrefix}-frequency-${option.value}`}
							>
								{option.label}
							</button>
						))}
					</div>
				</div>

				{classPrefix ? (
					<div className={cls(['field', 'field--interval'], '')}>{intervalInput}</div>
				) : (
					intervalInput
				)}
			</div>

			{/* Week of Month — monthly patterns only */}
			{isMonthly && (
				<div className={cls(['field', 'field--week-ordinals'], 'flex flex-col gap-1 w-full')}>
					<span className={labelClassName}>Week of Month {requiredMark}</span>
					<div className={cls(['chips'], 'flex flex-wrap gap-2')}>
						{WEEK_ORDINAL_OPTIONS.map(ordinal => {
							const isSelected = weekOrdinals.includes(ordinal.value);
							return (
								<button
									key={ordinal.value}
									type="button"
									aria-pressed={isSelected}
									onClick={() => toggleWeekOrdinal(ordinal.value)}
									className={pill('chip', isSelected, 'px-4')}
									data-test-id={`${testIdPrefix}-week-ordinal-${ordinal.value}`}
								>
									{ordinal.label}
								</button>
							);
						})}
					</div>
					<ErrorMessage error={errors?.weekOrdinals} />
				</div>
			)}

			{/* Days of Week */}
			<div className={cls(['field', 'field--days'], 'flex flex-col gap-1 w-full')}>
				<span className={labelClassName}>
					{isMonthly ? 'Which Day' : 'Days of Week'} {requiredMark}
				</span>
				{isMonthly && (
					<span className={cls(['help'], 'text-sm text-gray-500')}>
						Paired with the week above — 1st and Friday is the first Friday of the month.
					</span>
				)}
				<div className={cls(['days'], 'flex flex-wrap gap-2')}>
					{DAY_OPTIONS.map(day => {
						const isSelected = daysOfWeek.includes(day.value);
						return (
							<button
								key={day.value}
								type="button"
								aria-pressed={isSelected}
								aria-label={classPrefix ? day.name : undefined}
								onClick={() => toggleDay(day.value)}
								className={pill('day', isSelected, 'w-10')}
								data-test-id={`${testIdPrefix}-day-${day.value}`}
							>
								{day.label}
							</button>
						);
					})}
				</div>
				<ErrorMessage error={errors?.daysOfWeek} />
			</div>

			{summary && !hideSummary && <p className="text-sm text-gray-600 -mt-2">{summary}</p>}
		</>
	);
}
